import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { RolesAdminService } from '@/core/api/sicom-api/sistema/roles/roles-admin.service';
import { RoleDTO } from '@/core/api/sicom-api/sistema/roles/roles-admin.model';
import { agruparRoles, FuncionalidadeGrupo, ModuloGrupo } from '../roles-agrupamento.util';
import { RoleDetalhe, RoleEmEdicao } from '../role-detalhe/role-detalhe';

@Component({
    selector: 'app-roles-catalogo',
    standalone: true,
    imports: [FormsModule, TableModule, ButtonModule, TooltipModule, InputTextModule, IconFieldModule, InputIconModule, ConfirmDialogModule, CardContent, RoleDetalhe],
    providers: [ConfirmationService],
    templateUrl: './roles-catalogo.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Catálogo de roles agrupadas por módulo/funcionalidade (kanban) com filtro, criação, edição e exclusão. */
export class RolesCatalogo implements OnInit {
    private readonly service = inject(RolesAdminService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirm = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly roles = signal<RoleDTO[]>([]);
    protected readonly carregando = signal(false);
    protected readonly filtro = signal('');
    protected readonly modalNova = signal(false);
    protected readonly roleEmEdicao = signal<RoleEmEdicao | null>(null);

    protected readonly grupos = computed<ModuloGrupo[]>(() => {
        const termo = this.filtro().toLowerCase().trim();
        const lista = termo ? this.roles().filter((r) => r.name.toLowerCase().includes(termo) || (r.description ?? '').toLowerCase().includes(termo)) : this.roles();
        return agruparRoles(lista);
    });
    protected readonly modulosExistentes = computed(() => this.grupos().map((g) => g.modulo));
    protected readonly funcsExistentes = computed(() => [...new Set(this.grupos().flatMap((g) => g.funcionalidades.map((f) => f.funcionalidade)))]);

    // ── Kanban: seleção (com fallback no 1º para sobreviver ao filtro) ──
    protected readonly moduloSel = signal<string | null>(null);
    protected readonly funcSel = signal<string | null>(null);
    protected readonly moduloAtual = computed<ModuloGrupo | null>(() => {
        const gs = this.grupos();
        return gs.find((m) => m.modulo === this.moduloSel()) ?? gs[0] ?? null;
    });
    protected readonly funcAtual = computed<FuncionalidadeGrupo | null>(() => {
        const mod = this.moduloAtual();
        return mod?.funcionalidades.find((f) => f.funcionalidade === this.funcSel()) ?? mod?.funcionalidades[0] ?? null;
    });

    ngOnInit(): void {
        this.carregar();
    }

    protected carregar(): void {
        this.carregando.set(true);
        this.service
            .listarRoles()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (rs) => {
                    this.roles.set(rs);
                    this.carregando.set(false);
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar roles' });
                    this.carregando.set(false);
                }
            });
    }

    protected selecionarModulo(mod: ModuloGrupo): void {
        this.moduloSel.set(mod.modulo);
        this.funcSel.set(mod.funcionalidades[0]?.funcionalidade ?? null);
    }
    protected selecionarFunc(f: FuncionalidadeGrupo): void {
        this.funcSel.set(f.funcionalidade);
    }

    protected abrirNova(): void {
        this.roleEmEdicao.set(null);
        this.modalNova.set(true);
    }
    protected abrirEdicao(role: string, descricao?: string): void {
        this.roleEmEdicao.set({ name: role, description: descricao });
        this.modalNova.set(true);
    }
    protected onRoleSalva(): void {
        this.modalNova.set(false);
        this.roleEmEdicao.set(null);
        this.carregar();
    }

    protected confirmarExcluir(role: string): void {
        this.confirm.confirm({
            header: 'Excluir role',
            message: `Excluir "${role}"? Ela será removida do catálogo e desvinculada de todos os usuários.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Excluir',
            rejectLabel: 'Cancelar',
            accept: () => this.excluir(role)
        });
    }
    private excluir(role: string): void {
        this.service
            .excluirRole(role)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Role excluída.' });
                    this.carregar();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao excluir role' })
            });
    }
}
